import { z } from 'zod';
import { idSchema, timestampsSchema } from './common';
import { generatedIntegrationActionCatalog as _generatedIntegrationActionCatalog } from './generated-integration-catalog';
/** Generated integration action catalog from OpenAPI specs (e.g. GitHub, ArgoCD). */
export const generatedIntegrationActionCatalog = _generatedIntegrationActionCatalog;

export const integrationProviders = [
  'slack',
  'gmail',
  'google_workspace',
  'microsoft_365',
  'github',
  'jira',
  'confluence',
  'salesforce',
  'hubspot',
  'notion',
  'http',
  'webhook',
  'sql',
  'sharepoint',
  'azure_devops',
  'teams',
  'email',
  'argocd',
  'grafana',
  'prometheus',
  'sentry',
] as const;
export const integrationProviderSchema = z.enum(integrationProviders);
export type IntegrationProvider = z.infer<typeof integrationProviderSchema>;

export const integrationKeySchema = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[a-z0-9_.-]+$/, 'integration key must be kebab/snake case');

export interface IntegrationAction {
  key: string;
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  requiresApproval?: boolean;
  http?: {
    method: string;
    path: string;
    requestBodyContentType?: string;
  };
}

export type IntegrationCatalogToolMode = 'discover' | 'describe' | 'call';

const GITHUB_REPO_URL_RE = /github\.com\/([^/\s]+)\/([^/\s.]+)(?:\.git)?(?:\/|$)/i;

/** Parse `https://github.com/owner/repo` (or `.git`) into owner/repo slugs. */
export function parseGitHubRepoUrl(
  url: string,
): { owner: string; repo: string; slug: string } | undefined {
  const match = url.trim().match(GITHUB_REPO_URL_RE);
  if (!match) return undefined;
  return { owner: match[1], repo: match[2], slug: `${match[1]}/${match[2]}` };
}

/**
 * Resolve the GitHub repository an integration call targets. Integration config
 * (owner/repo set by the tenant) wins over agent-supplied params — agents often
 * guess wrong org names. Workspace `settings.repository` is the next fallback.
 */
export function resolveGitHubRepo(
  input: Record<string, unknown>,
  config: Record<string, unknown>,
  workspaceRepositoryUrl?: string,
): { owner: string; repo: string; slug: string; url: string } | null {
  const configOwner = typeof config['owner'] === 'string' ? config['owner'].trim() : '';
  const configRepo = typeof config['repo'] === 'string' ? config['repo'].trim() : '';
  if (configOwner && configRepo) {
    return {
      owner: configOwner,
      repo: configRepo,
      slug: `${configOwner}/${configRepo}`,
      url: `https://github.com/${configOwner}/${configRepo}`,
    };
  }

  const fromWorkspace = workspaceRepositoryUrl ? parseGitHubRepoUrl(workspaceRepositoryUrl) : undefined;
  if (fromWorkspace) {
    return {
      ...fromWorkspace,
      url: `https://github.com/${fromWorkspace.slug}`,
    };
  }

  const inputOwner = typeof input['owner'] === 'string' ? input['owner'].trim() : '';
  const inputRepo = typeof input['repo'] === 'string' ? input['repo'].trim() : '';
  if (inputOwner && inputRepo) {
    return {
      owner: inputOwner,
      repo: inputRepo,
      slug: `${inputOwner}/${inputRepo}`,
      url: `https://github.com/${inputOwner}/${inputRepo}`,
    };
  }

  return null;
}

export interface IntegrationActionSummary {
  key: string;
  name: string;
  description: string;
  requiresApproval: boolean;
}

export const integrationActionCatalog: Record<IntegrationProvider, IntegrationAction[]> = {
  slack: [
    {
      key: 'list_channels',
      name: 'List channels',
      description: 'List Slack conversations the bot can access.',
      inputSchema: {
        type: 'object',
        properties: {
          types: { type: 'string', description: 'Channel types (default: public_channel)' },
          limit: { type: 'number' },
        },
      },
      requiresApproval: false,
    },
    {
      key: 'get_channel_history',
      name: 'Get channel history',
      description: 'Fetch recent messages from a Slack channel.',
      inputSchema: {
        type: 'object',
        properties: {
          channel: { type: 'string' },
          limit: { type: 'number' },
        },
        required: ['channel'],
      },
      requiresApproval: false,
    },
    {
      key: 'post_message',
      name: 'Post message',
      description: 'Post a message to a Slack channel using chat.postMessage.',
      inputSchema: {
        type: 'object',
        properties: {
          channel: { type: 'string' },
          text: { type: 'string' },
        },
        required: ['channel', 'text'],
      },
      requiresApproval: true,
    },
    {
      key: 'update_message',
      name: 'Update message',
      description: 'Update an existing Slack message.',
      inputSchema: {
        type: 'object',
        properties: {
          channel: { type: 'string' },
          ts: { type: 'string' },
          text: { type: 'string' },
        },
        required: ['channel', 'ts', 'text'],
      },
      requiresApproval: true,
    },
    {
      key: 'add_reaction',
      name: 'Add reaction',
      description: 'Add an emoji reaction to a Slack message.',
      inputSchema: {
        type: 'object',
        properties: {
          channel: { type: 'string' },
          timestamp: { type: 'string' },
          name: { type: 'string', description: 'Emoji name without colons' },
        },
        required: ['channel', 'timestamp', 'name'],
      },
      requiresApproval: true,
    },
  ],
  gmail: [
    {
      key: 'list_messages',
      name: 'List messages',
      description: 'List Gmail messages matching an optional query.',
      inputSchema: {
        type: 'object',
        properties: {
          q: { type: 'string', description: 'Gmail search query' },
          maxResults: { type: 'number' },
        },
      },
      requiresApproval: false,
    },
    {
      key: 'get_message',
      name: 'Get message',
      description: 'Get a Gmail message by id.',
      inputSchema: {
        type: 'object',
        properties: {
          messageId: { type: 'string' },
        },
        required: ['messageId'],
      },
      requiresApproval: false,
    },
    {
      key: 'send_email',
      name: 'Send email',
      description: 'Send an email through the Gmail API.',
      inputSchema: {
        type: 'object',
        properties: {
          to: { type: 'string' },
          subject: { type: 'string' },
          body: { type: 'string' },
        },
        required: ['to', 'subject', 'body'],
      },
      requiresApproval: true,
    },
  ],
  google_workspace: [
    {
      key: 'list_events',
      name: 'List calendar events',
      description: 'List upcoming Google Calendar events.',
      inputSchema: {
        type: 'object',
        properties: {
          calendarId: { type: 'string', default: 'primary' },
          timeMin: { type: 'string' },
          timeMax: { type: 'string' },
          maxResults: { type: 'number' },
        },
      },
      requiresApproval: false,
    },
    {
      key: 'create_calendar_event',
      name: 'Create calendar event',
      description: 'Create an event in Google Calendar.',
      inputSchema: {
        type: 'object',
        properties: {
          calendarId: { type: 'string', default: 'primary' },
          summary: { type: 'string' },
          description: { type: 'string' },
          startDateTime: { type: 'string' },
          endDateTime: { type: 'string' },
          timeZone: { type: 'string' },
        },
        required: ['summary', 'startDateTime', 'endDateTime'],
      },
      requiresApproval: true,
    },
    {
      key: 'send_email',
      name: 'Send email',
      description: 'Send email through Gmail as part of Google Workspace.',
      inputSchema: {
        type: 'object',
        properties: {
          to: { type: 'string' },
          subject: { type: 'string' },
          body: { type: 'string' },
        },
        required: ['to', 'subject', 'body'],
      },
      requiresApproval: true,
    },
  ],
  microsoft_365: [
    {
      key: 'list_events',
      name: 'List calendar events',
      description: 'List Microsoft 365 calendar events.',
      inputSchema: {
        type: 'object',
        properties: {
          top: { type: 'number' },
        },
      },
      requiresApproval: false,
    },
    {
      key: 'send_mail',
      name: 'Send mail',
      description: 'Send mail through Microsoft Graph.',
      inputSchema: {
        type: 'object',
        properties: {
          to: { type: 'string' },
          subject: { type: 'string' },
          body: { type: 'string' },
        },
        required: ['to', 'subject', 'body'],
      },
      requiresApproval: true,
    },
  ],
  github: [
    {
      key: 'create_issue',
      name: 'Create issue',
      description: 'Create a GitHub issue in a configured or input repository.',
      inputSchema: {
        type: 'object',
        properties: {
          owner: { type: 'string' },
          repo: { type: 'string' },
          title: { type: 'string' },
          body: { type: 'string' },
        },
        required: ['title'],
      },
      requiresApproval: true,
    },
  ],
  jira: [
    {
      key: 'search_issues',
      name: 'Search issues',
      description: 'Search Jira issues with JQL.',
      inputSchema: {
        type: 'object',
        properties: {
          jql: { type: 'string' },
          maxResults: { type: 'number' },
        },
        required: ['jql'],
      },
      requiresApproval: false,
    },
    {
      key: 'get_issue',
      name: 'Get issue',
      description: 'Get a Jira issue by key.',
      inputSchema: {
        type: 'object',
        properties: {
          issueKey: { type: 'string' },
        },
        required: ['issueKey'],
      },
      requiresApproval: false,
    },
    {
      key: 'create_issue',
      name: 'Create issue',
      description: 'Create a Jira issue.',
      inputSchema: {
        type: 'object',
        properties: {
          projectKey: { type: 'string' },
          issueType: { type: 'string', default: 'Task' },
          summary: { type: 'string' },
          description: { type: 'string' },
        },
        required: ['summary'],
      },
      requiresApproval: true,
    },
    {
      key: 'update_issue',
      name: 'Update issue',
      description: 'Update fields on a Jira issue.',
      inputSchema: {
        type: 'object',
        properties: {
          issueKey: { type: 'string' },
          fields: { type: 'object' },
        },
        required: ['issueKey', 'fields'],
      },
      requiresApproval: true,
    },
    {
      key: 'add_comment',
      name: 'Add comment',
      description: 'Add a comment to a Jira issue.',
      inputSchema: {
        type: 'object',
        properties: {
          issueKey: { type: 'string' },
          body: { type: 'string' },
        },
        required: ['issueKey', 'body'],
      },
      requiresApproval: true,
    },
    {
      key: 'transition_issue',
      name: 'Transition issue',
      description: 'Transition a Jira issue to another status.',
      inputSchema: {
        type: 'object',
        properties: {
          issueKey: { type: 'string' },
          transitionId: { type: 'string' },
        },
        required: ['issueKey', 'transitionId'],
      },
      requiresApproval: true,
    },
  ],
  confluence: [
    {
      key: 'get_page',
      name: 'Get page',
      description: 'Get a Confluence page by id.',
      inputSchema: {
        type: 'object',
        properties: {
          pageId: { type: 'string' },
        },
        required: ['pageId'],
      },
      requiresApproval: false,
    },
    {
      key: 'search_content',
      name: 'Search content',
      description: 'Search Confluence content with CQL.',
      inputSchema: {
        type: 'object',
        properties: {
          cql: { type: 'string' },
          limit: { type: 'number' },
        },
        required: ['cql'],
      },
      requiresApproval: false,
    },
    {
      key: 'create_page',
      name: 'Create page',
      description: 'Create a Confluence page.',
      inputSchema: {
        type: 'object',
        properties: {
          spaceId: { type: 'string' },
          title: { type: 'string' },
          body: { type: 'string' },
          parentId: { type: 'string' },
        },
        required: ['title', 'body'],
      },
      requiresApproval: true,
    },
    {
      key: 'update_page',
      name: 'Update page',
      description: 'Update a Confluence page title/body.',
      inputSchema: {
        type: 'object',
        properties: {
          pageId: { type: 'string' },
          title: { type: 'string' },
          body: { type: 'string' },
          version: { type: 'number' },
        },
        required: ['pageId', 'title', 'body', 'version'],
      },
      requiresApproval: true,
    },
  ],
  salesforce: [
    {
      key: 'query',
      name: 'SOQL query',
      description: 'Run a SOQL query against Salesforce.',
      inputSchema: {
        type: 'object',
        properties: {
          q: { type: 'string', description: 'SOQL query string' },
        },
        required: ['q'],
      },
      requiresApproval: false,
    },
    {
      key: 'get_record',
      name: 'Get record',
      description: 'Get a Salesforce sObject record by id.',
      inputSchema: {
        type: 'object',
        properties: {
          objectApiName: { type: 'string' },
          recordId: { type: 'string' },
        },
        required: ['objectApiName', 'recordId'],
      },
      requiresApproval: false,
    },
    {
      key: 'create_record',
      name: 'Create record',
      description: 'Create a Salesforce sObject record.',
      inputSchema: {
        type: 'object',
        properties: {
          objectApiName: { type: 'string' },
          fields: { type: 'object' },
        },
        required: ['objectApiName', 'fields'],
      },
      requiresApproval: true,
    },
    {
      key: 'update_record',
      name: 'Update record',
      description: 'Update a Salesforce sObject record.',
      inputSchema: {
        type: 'object',
        properties: {
          objectApiName: { type: 'string' },
          recordId: { type: 'string' },
          fields: { type: 'object' },
        },
        required: ['objectApiName', 'recordId', 'fields'],
      },
      requiresApproval: true,
    },
  ],
  hubspot: [
    {
      key: 'search_contacts',
      name: 'Search contacts',
      description: 'Search HubSpot contacts.',
      inputSchema: {
        type: 'object',
        properties: {
          query: { type: 'string' },
          limit: { type: 'number' },
        },
      },
      requiresApproval: false,
    },
    {
      key: 'get_contact',
      name: 'Get contact',
      description: 'Get a HubSpot contact by id.',
      inputSchema: {
        type: 'object',
        properties: {
          contactId: { type: 'string' },
        },
        required: ['contactId'],
      },
      requiresApproval: false,
    },
    {
      key: 'create_contact',
      name: 'Create contact',
      description: 'Create a HubSpot contact.',
      inputSchema: {
        type: 'object',
        properties: {
          properties: { type: 'object' },
        },
        required: ['properties'],
      },
      requiresApproval: true,
    },
    {
      key: 'update_contact',
      name: 'Update contact',
      description: 'Update a HubSpot contact.',
      inputSchema: {
        type: 'object',
        properties: {
          contactId: { type: 'string' },
          properties: { type: 'object' },
        },
        required: ['contactId', 'properties'],
      },
      requiresApproval: true,
    },
  ],
  notion: [
    {
      key: 'search',
      name: 'Search',
      description: 'Search Notion pages and databases.',
      inputSchema: {
        type: 'object',
        properties: {
          query: { type: 'string' },
        },
      },
      requiresApproval: false,
    },
    {
      key: 'get_page',
      name: 'Get page',
      description: 'Get a Notion page by id.',
      inputSchema: {
        type: 'object',
        properties: {
          pageId: { type: 'string' },
        },
        required: ['pageId'],
      },
      requiresApproval: false,
    },
    {
      key: 'create_page',
      name: 'Create page',
      description: 'Create a Notion page under a configured parent.',
      inputSchema: {
        type: 'object',
        properties: {
          parentPageId: { type: 'string' },
          parentDatabaseId: { type: 'string' },
          title: { type: 'string' },
          markdown: { type: 'string' },
          properties: { type: 'object' },
        },
        required: ['title'],
      },
      requiresApproval: true,
    },
    {
      key: 'update_page',
      name: 'Update page',
      description: 'Update Notion page properties.',
      inputSchema: {
        type: 'object',
        properties: {
          pageId: { type: 'string' },
          properties: { type: 'object' },
        },
        required: ['pageId', 'properties'],
      },
      requiresApproval: true,
    },
  ],
  http: [
    {
      key: 'request',
      name: 'HTTP request',
      description: 'Send an allowlisted HTTP request through this named connection.',
      inputSchema: {
        type: 'object',
        properties: {
          method: { type: 'string' },
          path: { type: 'string' },
          url: { type: 'string' },
          headers: { type: 'object' },
          body: {},
        },
      },
      requiresApproval: true,
    },
  ],
  webhook: [
    {
      key: 'send',
      name: 'Send webhook',
      description: 'Send a JSON payload to the configured webhook URL.',
      inputSchema: {
        type: 'object',
        properties: {
          payload: {},
        },
        required: ['payload'],
      },
      requiresApproval: true,
    },
  ],
  sql: [
    {
      key: 'query',
      name: 'Run SQL query',
      description: 'Run a SQL query against the configured database connection.',
      inputSchema: {
        type: 'object',
        properties: {
          query: { type: 'string' },
          params: { type: 'array', items: {} },
        },
        required: ['query'],
      },
      requiresApproval: true,
    },
  ],
  sharepoint: [
    {
      key: 'list_items',
      name: 'List items',
      description: 'List SharePoint list items.',
      inputSchema: {
        type: 'object',
        properties: {
          siteId: { type: 'string' },
          listId: { type: 'string' },
        },
      },
      requiresApproval: false,
    },
    {
      key: 'get_item',
      name: 'Get item',
      description: 'Get a SharePoint list item by id.',
      inputSchema: {
        type: 'object',
        properties: {
          siteId: { type: 'string' },
          listId: { type: 'string' },
          itemId: { type: 'string' },
        },
        required: ['itemId'],
      },
      requiresApproval: false,
    },
    {
      key: 'create_list_item',
      name: 'Create list item',
      description: 'Create a SharePoint list item through Microsoft Graph.',
      inputSchema: {
        type: 'object',
        properties: {
          siteId: { type: 'string' },
          listId: { type: 'string' },
          fields: { type: 'object' },
        },
        required: ['fields'],
      },
      requiresApproval: true,
    },
    {
      key: 'update_list_item',
      name: 'Update list item',
      description: 'Update a SharePoint list item.',
      inputSchema: {
        type: 'object',
        properties: {
          siteId: { type: 'string' },
          listId: { type: 'string' },
          itemId: { type: 'string' },
          fields: { type: 'object' },
        },
        required: ['itemId', 'fields'],
      },
      requiresApproval: true,
    },
  ],
  azure_devops: [
    {
      key: 'list_work_items',
      name: 'List work items',
      description: 'Query Azure DevOps work items with WIQL.',
      inputSchema: {
        type: 'object',
        properties: {
          organization: { type: 'string' },
          project: { type: 'string' },
          wiql: { type: 'string' },
        },
        required: ['wiql'],
      },
      requiresApproval: false,
    },
    {
      key: 'get_work_item',
      name: 'Get work item',
      description: 'Get an Azure DevOps work item by id.',
      inputSchema: {
        type: 'object',
        properties: {
          organization: { type: 'string' },
          project: { type: 'string' },
          id: { type: 'number' },
        },
        required: ['id'],
      },
      requiresApproval: false,
    },
    {
      key: 'create_work_item',
      name: 'Create work item',
      description: 'Create an Azure DevOps work item.',
      inputSchema: {
        type: 'object',
        properties: {
          organization: { type: 'string' },
          project: { type: 'string' },
          workItemType: { type: 'string', default: 'Task' },
          title: { type: 'string' },
          description: { type: 'string' },
        },
        required: ['title'],
      },
      requiresApproval: true,
    },
    {
      key: 'update_work_item',
      name: 'Update work item',
      description: 'Update an Azure DevOps work item.',
      inputSchema: {
        type: 'object',
        properties: {
          organization: { type: 'string' },
          project: { type: 'string' },
          id: { type: 'number' },
          title: { type: 'string' },
          description: { type: 'string' },
        },
        required: ['id'],
      },
      requiresApproval: true,
    },
  ],
  teams: [
    {
      key: 'list_channels',
      name: 'List channels',
      description: 'List channels in a Microsoft Teams team.',
      inputSchema: {
        type: 'object',
        properties: {
          teamId: { type: 'string' },
        },
      },
      requiresApproval: false,
    },
    {
      key: 'send_message',
      name: 'Send message',
      description: 'Send a Teams channel message through Microsoft Graph or an incoming webhook.',
      inputSchema: {
        type: 'object',
        properties: {
          teamId: { type: 'string' },
          channelId: { type: 'string' },
          text: { type: 'string' },
        },
        required: ['text'],
      },
      requiresApproval: true,
    },
  ],
  email: [
    {
      key: 'send_email',
      name: 'Send email',
      description: 'Send an email through the configured email API endpoint.',
      inputSchema: {
        type: 'object',
        properties: {
          to: { type: 'string' },
          subject: { type: 'string' },
          body: { type: 'string' },
        },
        required: ['to', 'subject', 'body'],
      },
      requiresApproval: true,
    },
  ],
  // ── Actions defined in generated-integration-catalog.ts ─────
  argocd: [],
  grafana: [],
  prometheus: [],
  sentry: [],
};

/** Merge base + generated catalogs; generated entries win on key collision. */
export function getIntegrationActions(provider: IntegrationProvider): IntegrationAction[] {
  const base = integrationActionCatalog[provider] ?? [];
  const generated = generatedIntegrationActionCatalog[provider] ?? [];
  if (!generated.length) return base;
  if (!base.length) return generated;
  const byKey = new Map<string, IntegrationAction>();
  for (const action of base) byKey.set(action.key, action);
  for (const action of generated) byKey.set(action.key, action);
  return [...byKey.values()];
}

export function getIntegrationAction(
  provider: IntegrationProvider,
  actionKey: string,
): IntegrationAction | undefined {
  return getIntegrationActions(provider).find((action) => action.key === actionKey);
}

/** Resolve whether an action requires approval, honoring per-integration overrides. */
export function effectiveRequiresApproval(
  action: IntegrationAction,
  config: Record<string, unknown> = {},
): boolean {
  const overrides = record(config['toolApprovalOverrides']);
  const override = overrides[action.key];
  if (typeof override === 'boolean') return override;
  return action.requiresApproval ?? true;
}

export interface IntegrationToolView {
  key: string;
  name: string;
  description: string;
  requiresApproval: boolean;
  defaultRequiresApproval: boolean;
}

/** Effective tool list for an integration instance (catalog + approval overrides). */
export function listIntegrationTools(
  provider: IntegrationProvider,
  config: Record<string, unknown> = {},
): IntegrationToolView[] {
  return getIntegrationActions(provider).map((action) => {
    const defaultRequiresApproval = action.requiresApproval ?? true;
    return {
      key: action.key,
      name: action.name,
      description: action.description,
      defaultRequiresApproval,
      requiresApproval: effectiveRequiresApproval(action, config),
    };
  });
}

export function discoverIntegrationActions(
  provider: IntegrationProvider,
  query = '',
  limit = 20,
): IntegrationActionSummary[] {
  const normalized = query.trim().toLowerCase();
  const actions = normalized
    ? getIntegrationActions(provider).filter((action) =>
        [action.key, action.name, action.description].join(' ').toLowerCase().includes(normalized),
      )
    : getIntegrationActions(provider);
  return actions.slice(0, Math.max(1, limit)).map((action) => ({
    key: action.key,
    name: action.name,
    description: action.description,
    requiresApproval: action.requiresApproval ?? true,
  }));
}

export function describeIntegrationAction(
  provider: IntegrationProvider,
  actionKey: string,
): IntegrationAction {
  const action = getIntegrationAction(provider, actionKey);
  if (!action) throw new Error(`Unknown ${provider} integration action "${actionKey}"`);
  return action;
}

export function buildIntegrationToolKey(integrationKey: string, actionKey: string): string {
  return `integration_${integrationKey}_${actionKey}`.replace(/[^a-zA-Z0-9_]/g, '_');
}

export function buildIntegrationCatalogToolKey(
  integrationKey: string,
  mode: IntegrationCatalogToolMode,
): string {
  return `integration_i_${hexEncode(integrationKey)}_${mode}`;
}

export function isIntegrationCatalogToolKey(toolKey: string): boolean {
  return /^integration_i_[0-9a-f]+_(discover|describe|call)$/.test(toolKey);
}

/** First-class per-action tool keys (not the legacy discover/describe/call meta tools). */
export function isIntegrationActionToolKey(toolKey: string): boolean {
  return /^integration_[a-zA-Z0-9_]+_.+$/.test(toolKey) && !isIntegrationCatalogToolKey(toolKey);
}

const HTTP_METHODS = ['get', 'post', 'put', 'patch', 'delete'] as const;

export function buildIntegrationActionsFromOpenApi(
  provider: IntegrationProvider,
  spec: Record<string, unknown>,
): IntegrationAction[] {
  const paths = record(spec['paths']);
  const actions: IntegrationAction[] = [];
  for (const [path, pathItem] of Object.entries(paths)) {
    const pathRecord = record(pathItem);
    const pathParameters = arrayOfRecords(pathRecord['parameters']);
    for (const method of HTTP_METHODS) {
      const operation = record(pathRecord[method]);
      if (Object.keys(operation).length === 0) continue;
      const operationParameters = [...pathParameters, ...arrayOfRecords(operation['parameters'])];
      const requestBody = record(operation['requestBody']);
      const { schema: bodySchema, contentType } = firstJsonLikeRequestBodySchema(requestBody);
      const key = openApiActionKey(operation, method, path);
      const inputSchema = openApiInputSchema(operationParameters, bodySchema, requestBody['required'] === true);
      actions.push({
        key,
        name: str(operation['summary'], key.replace(/_/g, ' ')),
        description: str(operation['description'], str(operation['summary'], `${method.toUpperCase()} ${path}`)),
        inputSchema,
        requiresApproval: method !== 'get',
        http: {
          method: method.toUpperCase(),
          path,
          requestBodyContentType: contentType,
        },
      });
    }
  }

  // OpenAPI documents can contain duplicate operationIds across tags/versions.
  const seen = new Map<string, number>();
  return actions.map((action) => {
    const count = seen.get(action.key) ?? 0;
    seen.set(action.key, count + 1);
    return count === 0 ? action : { ...action, key: `${action.key}_${count + 1}` };
  });
}

export type IntegrationExecutionPlan =
  | {
      kind: 'http';
      method: string;
      url: string;
      headers: Record<string, string>;
      body?: unknown;
    }
  | {
      kind: 'sql';
      query: string;
      params: unknown[];
    };

/** True for full Git commit object IDs (SHA-1 or SHA-256). */
export function isGitCommitSha(value: string): boolean {
  return /^[0-9a-f]{40}$/i.test(value) || /^[0-9a-f]{64}$/i.test(value);
}

/**
 * Map friendly create_branch fields onto GitHub's git refs API body
 * (`ref` + `sha`). Accepts either the catalog shape (`branchName`/`fromRef`)
 * or the upstream field names.
 */
export function normalizeGitHubCreateBranchInput(
  input: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...input };
  const branchName = optionalString(next['branchName']);
  const fromRef = optionalString(next['fromRef']);
  delete next['branchName'];
  delete next['fromRef'];

  let ref = optionalString(next['ref']) ?? branchName;
  if (ref && !ref.startsWith('refs/')) {
    ref = `refs/heads/${ref}`;
  }
  if (ref) next['ref'] = ref;

  const sha = optionalString(next['sha']) ?? fromRef ?? 'main';
  next['sha'] = sha;
  return next;
}

/**
 * True when `value` is already valid Base64 (GitHub Contents API shape).
 * Plain source text (newlines, braces, etc.) returns false so we can encode it.
 */
export function isLikelyBase64Content(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed || trimmed.length % 4 !== 0) return false;
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(trimmed)) return false;
  try {
    const buf = Buffer.from(trimmed, 'base64');
    const reencoded = buf.toString('base64');
    return reencoded === trimmed || reencoded.replace(/=+$/, '') === trimmed.replace(/=+$/, '');
  } catch {
    return false;
  }
}

/**
 * GitHub Contents API requires `content` as Base64. Agents almost always pass
 * plain file text — encode it when it is not already Base64.
 */
export function normalizeGitHubCommitFileInput(
  input: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...input };
  const content = optionalString(next['content']);
  if (content !== undefined && !isLikelyBase64Content(content)) {
    next['content'] = Buffer.from(content, 'utf8').toString('base64');
  }
  return next;
}

/**
 * Map catalog camelCase merge fields onto GitHub's snake_case merge body.
 * (`merge_method`, `commit_title`, `commit_message`).
 */
export function normalizeGitHubMergePullRequestInput(
  input: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...input };
  const mergeMethod = optionalString(next['mergeMethod']) ?? optionalString(next['merge_method']);
  const commitTitle = optionalString(next['commitTitle']) ?? optionalString(next['commit_title']);
  const commitMessage =
    optionalString(next['commitMessage']) ?? optionalString(next['commit_message']);
  delete next['mergeMethod'];
  delete next['commitTitle'];
  delete next['commitMessage'];
  if (mergeMethod) next['merge_method'] = mergeMethod;
  if (commitTitle) next['commit_title'] = commitTitle;
  if (commitMessage) next['commit_message'] = commitMessage;
  return next;
}

/**
 * Argo CD rollback body uses `id` (deployment history id), not `revisionId`.
 */
export function normalizeArgoCdRollbackInput(
  input: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...input };
  const revisionId = next['revisionId'] ?? next['id'];
  delete next['revisionId'];
  if (revisionId !== undefined && revisionId !== null && revisionId !== '') {
    next['id'] = typeof revisionId === 'number' ? revisionId : Number(revisionId);
  }
  return next;
}

/**
 * Grafana annotations API expects `dashboardUID` (capital UID).
 */
export function normalizeGrafanaCreateAnnotationInput(
  input: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...input };
  const uid = optionalString(next['dashboardUID']) ?? optionalString(next['dashboardUid']);
  delete next['dashboardUid'];
  if (uid) next['dashboardUID'] = uid;
  return next;
}

/**
 * Prometheus label-values accepts repeated `match[]` selectors.
 */
export function normalizePrometheusListMetricsInput(
  input: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...input };
  const match = optionalString(next['match']) ?? optionalString(next['match[]']);
  delete next['match'];
  if (match) next['match[]'] = match;
  return next;
}

function normalizeIntegrationActionInput(
  provider: IntegrationProvider,
  actionKey: string,
  input: Record<string, unknown>,
): Record<string, unknown> {
  if (provider === 'github' && actionKey === 'create_branch') {
    return normalizeGitHubCreateBranchInput(input);
  }
  if (provider === 'github' && actionKey === 'commit_file') {
    return normalizeGitHubCommitFileInput(input);
  }
  if (provider === 'github' && actionKey === 'merge_pull_request') {
    return normalizeGitHubMergePullRequestInput(input);
  }
  if (provider === 'argocd' && actionKey === 'rollback_app') {
    return normalizeArgoCdRollbackInput(input);
  }
  if (provider === 'grafana' && actionKey === 'create_annotation') {
    return normalizeGrafanaCreateAnnotationInput(input);
  }
  if (provider === 'prometheus' && actionKey === 'list_metrics') {
    return normalizePrometheusListMetricsInput(input);
  }
  return input;
}

export function buildIntegrationExecutionPlan(
  provider: IntegrationProvider,
  actionKey: string,
  config: Record<string, unknown>,
  input: Record<string, unknown>,
  token?: string,
): IntegrationExecutionPlan {
  const action = describeIntegrationAction(provider, actionKey);
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (token) headers['authorization'] = bearer(token);

  if (action.http) {
    const plan = buildOpenApiExecutionPlan(
      provider,
      action,
      config,
      normalizeIntegrationActionInput(provider, actionKey, input),
      headers,
    );
    // Same path as get_pull_request; GitHub returns the unified diff only when
    // the Accept header asks for the diff media type.
    if (provider === 'github' && actionKey === 'get_pull_request_diff' && plan.kind === 'http') {
      return {
        ...plan,
        headers: {
          ...plan.headers,
          accept: 'application/vnd.github.v3.diff',
        },
      };
    }
    return plan;
  }

  switch (provider) {
    case 'slack': {
      const slackBase = str(config['baseUrl'], 'https://slack.com/api');
      switch (actionKey) {
        case 'list_channels':
          return http('POST', joinUrl(slackBase, '/conversations.list'), headers, {
            types: str(input['types'], 'public_channel'),
            limit: input['limit'] ?? 100,
          });
        case 'get_channel_history':
          return http('POST', joinUrl(slackBase, '/conversations.history'), headers, {
            channel: str(input['channel'], str(config['channel'], '')),
            limit: input['limit'] ?? 50,
          });
        case 'update_message':
          return http('POST', joinUrl(slackBase, '/chat.update'), headers, {
            channel: str(input['channel'], str(config['channel'], '')),
            ts: str(input['ts']),
            text: str(input['text']),
          });
        case 'add_reaction':
          return http('POST', joinUrl(slackBase, '/reactions.add'), headers, {
            channel: str(input['channel'], str(config['channel'], '')),
            timestamp: str(input['timestamp']),
            name: str(input['name']),
          });
        case 'post_message':
        default:
          return http('POST', joinUrl(slackBase, '/chat.postMessage'), headers, {
            channel: str(input['channel'], str(config['channel'], '')),
            text: str(input['text']),
          });
      }
    }
    case 'gmail': {
      const gmailBase = str(config['baseUrl'], 'https://gmail.googleapis.com');
      switch (actionKey) {
        case 'list_messages': {
          const qs = new URLSearchParams();
          if (str(input['q'])) qs.set('q', str(input['q']));
          if (input['maxResults'] != null) qs.set('maxResults', String(input['maxResults']));
          const q = qs.toString();
          return http('GET', joinUrl(gmailBase, `/gmail/v1/users/me/messages${q ? `?${q}` : ''}`), headers);
        }
        case 'get_message':
          return http(
            'GET',
            joinUrl(gmailBase, `/gmail/v1/users/me/messages/${encodeURIComponent(str(input['messageId']))}`),
            headers,
          );
        case 'send_email':
        default:
          return http('POST', joinUrl(gmailBase, '/gmail/v1/users/me/messages/send'), headers, {
            raw: gmailRaw(input),
          });
      }
    }
    case 'google_workspace': {
      const calId = encodeURIComponent(str(input['calendarId'], str(config['calendarId'], 'primary')));
      const gwBase = str(config['baseUrl'], 'https://www.googleapis.com');
      switch (actionKey) {
        case 'list_events': {
          const qs = new URLSearchParams();
          if (str(input['timeMin'])) qs.set('timeMin', str(input['timeMin']));
          if (str(input['timeMax'])) qs.set('timeMax', str(input['timeMax']));
          if (input['maxResults'] != null) qs.set('maxResults', String(input['maxResults']));
          const q = qs.toString();
          return http('GET', joinUrl(gwBase, `/calendar/v3/calendars/${calId}/events${q ? `?${q}` : ''}`), headers);
        }
        case 'send_email':
          return http(
            'POST',
            joinUrl(str(config['gmailBaseUrl'], 'https://gmail.googleapis.com'), '/gmail/v1/users/me/messages/send'),
            headers,
            { raw: gmailRaw(input) },
          );
        case 'create_calendar_event':
        default:
          return http('POST', joinUrl(gwBase, `/calendar/v3/calendars/${calId}/events`), headers, {
            summary: str(input['summary']),
            description: str(input['description']),
            start: {
              dateTime: str(input['startDateTime']),
              timeZone: str(input['timeZone'], str(config['timeZone'], 'UTC')),
            },
            end: {
              dateTime: str(input['endDateTime']),
              timeZone: str(input['timeZone'], str(config['timeZone'], 'UTC')),
            },
          });
      }
    }
    case 'microsoft_365': {
      const graphBase = str(config['baseUrl'], 'https://graph.microsoft.com');
      switch (actionKey) {
        case 'list_events': {
          const top = input['top'] != null ? `?$top=${encodeURIComponent(String(input['top']))}` : '';
          return http('GET', joinUrl(graphBase, `/v1.0/me/events${top}`), headers);
        }
        case 'send_mail':
        default:
          return http('POST', joinUrl(graphBase, '/v1.0/me/sendMail'), headers, graphMailBody(input));
      }
    }
    case 'github':
      return http(
        'POST',
        joinUrl(
          str(config['baseUrl'], 'https://api.github.com'),
          `/repos/${str(input['owner'], str(config['owner']))}/${str(input['repo'], str(config['repo']))}/issues`,
        ),
        { ...headers, accept: 'application/vnd.github+json' },
        { title: str(input['title']), body: str(input['body']) },
      );
    case 'jira': {
      const jiraBase = str(config['baseUrl']);
      switch (actionKey) {
        case 'search_issues': {
          const qs = new URLSearchParams({ jql: str(input['jql']) });
          if (input['maxResults'] != null) qs.set('maxResults', String(input['maxResults']));
          return http('GET', joinUrl(jiraBase, `/rest/api/3/search?${qs}`), headers);
        }
        case 'get_issue':
          return http(
            'GET',
            joinUrl(jiraBase, `/rest/api/3/issue/${encodeURIComponent(str(input['issueKey']))}`),
            headers,
          );
        case 'update_issue':
          return http(
            'PUT',
            joinUrl(jiraBase, `/rest/api/3/issue/${encodeURIComponent(str(input['issueKey']))}`),
            headers,
            { fields: record(input['fields']) },
          );
        case 'add_comment':
          return http(
            'POST',
            joinUrl(jiraBase, `/rest/api/3/issue/${encodeURIComponent(str(input['issueKey']))}/comment`),
            headers,
            { body: str(input['body']) },
          );
        case 'transition_issue':
          return http(
            'POST',
            joinUrl(jiraBase, `/rest/api/3/issue/${encodeURIComponent(str(input['issueKey']))}/transitions`),
            headers,
            { transition: { id: str(input['transitionId']) } },
          );
        case 'create_issue':
        default:
          return http('POST', joinUrl(jiraBase, '/rest/api/3/issue'), headers, {
            fields: {
              project: { key: str(input['projectKey'], str(config['projectKey'])) },
              issuetype: { name: str(input['issueType'], str(config['issueType'], 'Task')) },
              summary: str(input['summary']),
              description: str(input['description']),
            },
          });
      }
    }
    case 'confluence': {
      const confBase = str(config['baseUrl']);
      switch (actionKey) {
        case 'get_page':
          return http(
            'GET',
            joinUrl(confBase, `/wiki/api/v2/pages/${encodeURIComponent(str(input['pageId']))}`),
            headers,
          );
        case 'search_content': {
          const qs = new URLSearchParams({ cql: str(input['cql']) });
          if (input['limit'] != null) qs.set('limit', String(input['limit']));
          return http('GET', joinUrl(confBase, `/wiki/rest/api/content/search?${qs}`), headers);
        }
        case 'update_page':
          return http(
            'PUT',
            joinUrl(confBase, `/wiki/api/v2/pages/${encodeURIComponent(str(input['pageId']))}`),
            headers,
            {
              id: str(input['pageId']),
              status: 'current',
              title: str(input['title']),
              body: { representation: 'storage', value: str(input['body']) },
              version: { number: Number(input['version']) },
            },
          );
        case 'create_page':
        default:
          return http('POST', joinUrl(confBase, '/wiki/api/v2/pages'), headers, {
            spaceId: str(input['spaceId'], str(config['spaceId'])),
            status: 'current',
            title: str(input['title']),
            parentId: optionalString(input['parentId'] ?? config['parentId']),
            body: { representation: 'storage', value: str(input['body']) },
          });
      }
    }
    case 'salesforce': {
      const sfBase = joinUrl(
        str(config['baseUrl']),
        `/services/data/${str(config['apiVersion'], 'v60.0')}`,
      );
      switch (actionKey) {
        case 'query':
          return http('GET', joinUrl(sfBase, `/query?q=${encodeURIComponent(str(input['q']))}`), headers);
        case 'get_record':
          return http(
            'GET',
            joinUrl(
              sfBase,
              `/sobjects/${encodeURIComponent(str(input['objectApiName']))}/${encodeURIComponent(str(input['recordId']))}`,
            ),
            headers,
          );
        case 'update_record':
          return http(
            'PATCH',
            joinUrl(
              sfBase,
              `/sobjects/${encodeURIComponent(str(input['objectApiName']))}/${encodeURIComponent(str(input['recordId']))}`,
            ),
            headers,
            record(input['fields']),
          );
        case 'create_record':
        default:
          return http(
            'POST',
            joinUrl(sfBase, `/sobjects/${encodeURIComponent(str(input['objectApiName']))}`),
            headers,
            record(input['fields']),
          );
      }
    }
    case 'hubspot': {
      const hsBase = str(config['baseUrl'], 'https://api.hubapi.com');
      switch (actionKey) {
        case 'search_contacts':
          return http('POST', joinUrl(hsBase, '/crm/v3/objects/contacts/search'), headers, {
            query: str(input['query']),
            limit: input['limit'] ?? 10,
          });
        case 'get_contact':
          return http(
            'GET',
            joinUrl(hsBase, `/crm/v3/objects/contacts/${encodeURIComponent(str(input['contactId']))}`),
            headers,
          );
        case 'update_contact':
          return http(
            'PATCH',
            joinUrl(hsBase, `/crm/v3/objects/contacts/${encodeURIComponent(str(input['contactId']))}`),
            headers,
            { properties: record(input['properties']) },
          );
        case 'create_contact':
        default:
          return http('POST', joinUrl(hsBase, '/crm/v3/objects/contacts'), headers, {
            properties: record(input['properties']),
          });
      }
    }
    case 'notion': {
      const notionHeaders = {
        ...headers,
        'notion-version': str(config['notionVersion'], '2022-06-28'),
      };
      const notionBase = str(config['baseUrl'], 'https://api.notion.com');
      switch (actionKey) {
        case 'search':
          return http('POST', joinUrl(notionBase, '/v1/search'), notionHeaders, {
            query: str(input['query']),
          });
        case 'get_page':
          return http(
            'GET',
            joinUrl(notionBase, `/v1/pages/${encodeURIComponent(str(input['pageId']))}`),
            notionHeaders,
          );
        case 'update_page':
          return http(
            'PATCH',
            joinUrl(notionBase, `/v1/pages/${encodeURIComponent(str(input['pageId']))}`),
            notionHeaders,
            { properties: record(input['properties']) },
          );
        case 'create_page':
        default:
          return http('POST', joinUrl(notionBase, '/v1/pages'), notionHeaders, notionPageBody(config, input));
      }
    }
    case 'http':
      if (/^https?:\/\//i.test(str(input['path']))) {
        throw new Error('HTTP integration path must be relative to the configured baseUrl');
      }
      return http(
        str(input['method'], str(config['method'], 'POST')).toUpperCase(),
        joinUrl(str(config['baseUrl']), str(input['path'], str(config['path'], ''))),
        { ...headers, ...stringRecord(config['headers']) },
        input['body'],
      );
    case 'webhook':
      return http('POST', str(config['webhookUrl'], str(config['url'])), headers, input['payload'] ?? input);
    case 'sql':
      return {
        kind: 'sql',
        query: str(input['query']),
        params: Array.isArray(input['params']) ? input['params'] : [],
      };
    case 'sharepoint': {
      const siteId = str(input['siteId'], str(config['siteId']));
      const listId = str(input['listId'], str(config['listId']));
      const spBase = str(config['baseUrl'], 'https://graph.microsoft.com');
      switch (actionKey) {
        case 'list_items':
          return http('GET', joinUrl(spBase, `/v1.0/sites/${siteId}/lists/${listId}/items`), headers);
        case 'get_item':
          return http(
            'GET',
            joinUrl(spBase, `/v1.0/sites/${siteId}/lists/${listId}/items/${encodeURIComponent(str(input['itemId']))}`),
            headers,
          );
        case 'update_list_item':
          return http(
            'PATCH',
            joinUrl(spBase, `/v1.0/sites/${siteId}/lists/${listId}/items/${encodeURIComponent(str(input['itemId']))}`),
            headers,
            { fields: record(input['fields']) },
          );
        case 'create_list_item':
        default:
          return http('POST', joinUrl(spBase, `/v1.0/sites/${siteId}/lists/${listId}/items`), headers, {
            fields: record(input['fields']),
          });
      }
    }
    case 'azure_devops': {
      const org = str(input['organization'], str(config['organization']));
      const project = str(input['project'], str(config['project']));
      const apiVersion = encodeURIComponent(str(config['apiVersion'], '7.1'));
      const adoBase = str(config['baseUrl'], 'https://dev.azure.com');
      switch (actionKey) {
        case 'list_work_items':
          return http(
            'POST',
            joinUrl(adoBase, `/${org}/${project}/_apis/wit/wiql?api-version=${apiVersion}`),
            headers,
            { query: str(input['wiql']) },
          );
        case 'get_work_item':
          return http(
            'GET',
            joinUrl(adoBase, `/${org}/${project}/_apis/wit/workitems/${Number(input['id'])}?api-version=${apiVersion}`),
            headers,
          );
        case 'update_work_item': {
          const ops: Array<{ op: string; path: string; value: string }> = [];
          if (str(input['title'])) ops.push({ op: 'add', path: '/fields/System.Title', value: str(input['title']) });
          if (str(input['description'])) {
            ops.push({ op: 'add', path: '/fields/System.Description', value: str(input['description']) });
          }
          return http(
            'PATCH',
            joinUrl(adoBase, `/${org}/${project}/_apis/wit/workitems/${Number(input['id'])}?api-version=${apiVersion}`),
            { ...headers, 'content-type': 'application/json-patch+json' },
            ops,
          );
        }
        case 'create_work_item':
        default:
          return http(
            'POST',
            joinUrl(
              adoBase,
              `/${org}/${project}/_apis/wit/workitems/$${encodeURIComponent(str(input['workItemType'], str(config['workItemType'], 'Task')))}?api-version=${apiVersion}`,
            ),
            { ...headers, 'content-type': 'application/json-patch+json' },
            [
              { op: 'add', path: '/fields/System.Title', value: str(input['title']) },
              { op: 'add', path: '/fields/System.Description', value: str(input['description']) },
            ],
          );
      }
    }
    case 'teams': {
      const graphBase = str(config['baseUrl'], 'https://graph.microsoft.com');
      switch (actionKey) {
        case 'list_channels':
          return http(
            'GET',
            joinUrl(graphBase, `/v1.0/teams/${str(input['teamId'], str(config['teamId']))}/channels`),
            headers,
          );
        case 'send_message':
        default:
          if (typeof config['webhookUrl'] === 'string' && config['webhookUrl']) {
            return http('POST', config['webhookUrl'], headers, { text: str(input['text']) });
          }
          return http(
            'POST',
            joinUrl(
              graphBase,
              `/v1.0/teams/${str(input['teamId'], str(config['teamId']))}/channels/${str(input['channelId'], str(config['channelId']))}/messages`,
            ),
            headers,
            { body: { contentType: 'html', content: str(input['text']) } },
          );
      }
    }
    case 'email':
      return http('POST', str(config['apiUrl'], str(config['url'])), headers, {
        to: str(input['to']),
        subject: str(input['subject']),
        body: str(input['body']),
      });
    // Providers below are handled by the action.http branch above
    // (actions defined in generated-integration-catalog.ts).
    case 'argocd':
    case 'grafana':
    case 'prometheus':
    case 'sentry':
      return http(
        action.http!.method,
        joinUrl(str(config['baseUrl']), action.http!.path),
        headers,
        input,
      );
  }
}

function buildOpenApiExecutionPlan(
  provider: IntegrationProvider,
  action: IntegrationAction,
  config: Record<string, unknown>,
  input: Record<string, unknown>,
  headers: Record<string, string>,
): IntegrationExecutionPlan {
  if (!action.http) throw new Error(`Integration action "${action.key}" has no HTTP metadata`);
  const baseUrl = str(config['baseUrl'], DEFAULT_PROVIDER_BASE_URLS[provider] ?? '');
  if (!baseUrl) {
    throw new Error(
      `Integration action "${action.key}" (${provider}) requires a configured "baseUrl" — ` +
        `set it on the integration's config before calling this action.`,
    );
  }

  // Path params are almost always passed flat (the tool's inputSchema declares them as
  // top-level properties, e.g. { owner, repo }, not nested under a "path" wrapper) — but
  // an explicit `input.path` object is honored first for callers that do nest.
  const pathInput = record(input['path']);
  const consumedKeys = new Set<string>();
  let renderedPath = action.http.path.replace(/\{([^}]+)\}/g, (_match, key: string) => {
    const configDefault = config[key];
    const hasConfigDefault =
      configDefault !== undefined && configDefault !== null && String(configDefault).trim() !== '';
    let value = pathInput[key] ?? input[key];
    // GitHub repo identity: tenant-configured owner/repo on the integration wins over
    // agent-supplied values (agents frequently guess the wrong org).
    if ((key === 'owner' || key === 'repo') && hasConfigDefault) {
      value = configDefault;
    } else if (value === undefined || value === null || value === '') {
      value = configDefault;
    }
    if (value === undefined || value === null || value === '') {
      throw new Error(`Missing path parameter "${key}" for integration action "${action.key}"`);
    }
    consumedKeys.add(key);
    // File paths may contain `/` — encode each segment but keep separators so
    // GitHub Contents / similar APIs receive `/repos/.../contents/apps/web/x.ts`.
    if (key === 'path') {
      return String(value)
        .split('/')
        .map((segment) => encodeURIComponent(segment))
        .join('/');
    }
    return encodeURIComponent(String(value));
  });

  const isReadMethod = action.http.method === 'GET' || action.http.method === 'DELETE';

  // Nested object wrappers (`input.query` / `input.body` as objects) override flat
  // assembly. Flat string fields literally named `query` or `body` (Prometheus PromQL,
  // Grafana search, PR description, etc.) must still flow through as normal properties.
  const explicitBody = input['body'];
  const isObjectBody =
    explicitBody !== undefined &&
    explicitBody !== null &&
    typeof explicitBody === 'object' &&
    !Array.isArray(explicitBody);
  const explicitQueryObj = record(input['query']);
  const hasObjectQuery = Object.keys(explicitQueryObj).length > 0;

  const remaining = () =>
    Object.fromEntries(
      Object.entries(input).filter(([key]) => {
        if (consumedKeys.has(key) || key === 'path') return false;
        if (key === 'query' && hasObjectQuery) return false;
        if (key === 'body' && isObjectBody) return false;
        return true;
      }),
    );

  // Query params: nested object `input.query` wins. Otherwise, for read methods,
  // use leftover flat properties (including a string field named `query`).
  const queryInput = hasObjectQuery ? explicitQueryObj : isReadMethod ? remaining() : {};
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(queryInput)) {
    if (value !== undefined && value !== null) query.set(key, String(value));
  }
  if (query.size > 0) renderedPath += `${renderedPath.includes('?') ? '&' : '?'}${query.toString()}`;

  // Body: nested object `input.body` wins; otherwise assemble from remaining flats.
  let body: unknown = isObjectBody ? explicitBody : undefined;
  if (body === undefined && !isReadMethod) {
    const rest = remaining();
    if (Object.keys(rest).length > 0) body = rest;
  }

  return http(action.http.method, joinUrl(baseUrl, renderedPath), {
    ...headers,
    ...(action.http.requestBodyContentType ? { 'content-type': action.http.requestBodyContentType } : {}),
    ...stringRecord(config['headers']),
  }, body);
}

function http(
  method: string,
  url: string,
  headers: Record<string, string>,
  body?: unknown,
): IntegrationExecutionPlan {
  return { kind: 'http', method, url, headers, body };
}

function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

/**
 * Default base URLs for SaaS providers whose API host never varies per tenant.
 * Self-hosted providers (argocd, grafana, prometheus, sentry) intentionally have
 * no default here — their baseUrl MUST come from the integration's config.
 */
const DEFAULT_PROVIDER_BASE_URLS: Partial<Record<IntegrationProvider, string>> = {
  github: 'https://api.github.com',
  slack: 'https://slack.com/api',
  hubspot: 'https://api.hubapi.com',
  notion: 'https://api.notion.com',
};

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function stringRecord(value: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, val] of Object.entries(record(value))) {
    if (typeof val === 'string') out[key] = val;
  }
  return out;
}

function arrayOfRecords(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(record).filter((item) => Object.keys(item).length > 0) : [];
}

function firstJsonLikeRequestBodySchema(requestBody: Record<string, unknown>): {
  schema?: Record<string, unknown>;
  contentType?: string;
} {
  const content = record(requestBody['content']);
  const preferred =
    content['application/json'] ??
    Object.entries(content).find(([type]) => type.includes('json'))?.[1] ??
    Object.values(content)[0];
  const mediaType = Object.entries(content).find(([, value]) => value === preferred)?.[0];
  const schema = record(record(preferred)['schema']);
  return {
    schema: Object.keys(schema).length ? schema : undefined,
    contentType: mediaType,
  };
}

function openApiInputSchema(
  parameters: Record<string, unknown>[],
  bodySchema: Record<string, unknown> | undefined,
  bodyRequired: boolean,
): Record<string, unknown> {
  const pathProperties: Record<string, unknown> = {};
  const queryProperties: Record<string, unknown> = {};
  const required: string[] = [];

  for (const parameter of parameters) {
    const name = str(parameter['name']);
    const location = str(parameter['in']);
    if (!name) continue;
    if (location === 'path') {
      pathProperties[name] = record(parameter['schema']);
      if (parameter['required'] === true) required.push(name);
    } else if (location === 'query') {
      queryProperties[name] = record(parameter['schema']);
    }
  }

  const properties: Record<string, unknown> = {
    ...pathProperties,
  };
  if (Object.keys(queryProperties).length > 0) {
    properties['query'] = { type: 'object', properties: queryProperties };
  }
  if (bodySchema) {
    properties['body'] = bodySchema;
    if (bodyRequired) required.push('body');
  }

  return {
    type: 'object',
    properties,
    ...(required.length ? { required } : {}),
  };
}

function openApiActionKey(operation: Record<string, unknown>, method: string, path: string): string {
  const operationId = str(operation['operationId']);
  const raw = operationId || `${method}_${path}`;
  return raw
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 120);
}

function joinUrl(base: string, path: string): string {
  if (!base) return path;
  if (!path) return base;
  return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

function bearer(token: string): string {
  return /^(Bearer|Basic|token)\s/i.test(token) ? token : `Bearer ${token}`;
}

function hexEncode(value: string): string {
  if (typeof Buffer !== 'undefined') return Buffer.from(value, 'utf8').toString('hex');
  return Array.from(value)
    .map((char) => char.charCodeAt(0).toString(16).padStart(2, '0'))
    .join('');
}

function gmailRaw(input: Record<string, unknown>): string {
  const message = [
    `To: ${str(input['to'])}`,
    `Subject: ${str(input['subject'])}`,
    'Content-Type: text/plain; charset="UTF-8"',
    '',
    str(input['body']),
  ].join('\r\n');
  const b64 =
    typeof Buffer !== 'undefined'
      ? Buffer.from(message, 'utf8').toString('base64')
      : btoa(unescape(encodeURIComponent(message)));
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function graphMailBody(input: Record<string, unknown>): Record<string, unknown> {
  return {
    message: {
      subject: str(input['subject']),
      body: { contentType: 'Text', content: str(input['body']) },
      toRecipients: [{ emailAddress: { address: str(input['to']) } }],
    },
    saveToSentItems: true,
  };
}

function notionPageBody(
  config: Record<string, unknown>,
  input: Record<string, unknown>,
): Record<string, unknown> {
  const parentDatabaseId = str(input['parentDatabaseId'], str(config['parentDatabaseId']));
  const parentPageId = str(input['parentPageId'], str(config['parentPageId']));
  const title = str(input['title']);
  return {
    parent: parentDatabaseId ? { database_id: parentDatabaseId } : { page_id: parentPageId },
    properties: {
      title: { title: [{ text: { content: title } }] },
      ...record(input['properties']),
    },
    children: str(input['markdown'])
      ? [
          {
            object: 'block',
            type: 'paragraph',
            paragraph: { rich_text: [{ type: 'text', text: { content: str(input['markdown']) } }] },
          },
        ]
      : [],
  };
}

export const integrationStatuses = [
  'connected',
  'disconnected',
  'error',
  'pending',
] as const;
export const integrationStatusSchema = z.enum(integrationStatuses);
export type IntegrationStatus = z.infer<typeof integrationStatusSchema>;

export const integrationSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    provider: integrationProviderSchema,
    key: integrationKeySchema,
    name: z.string().min(1).max(200),
    status: integrationStatusSchema.default('pending'),
    /** Vault reference holding this integration's credentials. Never the secret itself. */
    credentialsRef: z.string().optional(),
    /** Non-secret configuration (base urls, scopes, etc.). */
    config: z.record(z.unknown()).default({}),
    /** If this integration exposes an MCP server, its id in the registry. */
    mcpServerId: idSchema.optional(),
  })
  .merge(timestampsSchema);
export type Integration = z.infer<typeof integrationSchema>;

export const createIntegrationSchema = integrationSchema.pick({
  provider: true,
  key: true,
  name: true,
  config: true,
});
export type CreateIntegration = z.infer<typeof createIntegrationSchema>;

export const updateIntegrationSchema = createIntegrationSchema
  .omit({ provider: true, key: true })
  .partial()
  .extend({ status: integrationStatusSchema.optional() });
export type UpdateIntegration = z.infer<typeof updateIntegrationSchema>;

/** A discovered MCP server (pluggable extension). */
export const mcpServerSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    name: z.string().min(1).max(200),
    /**
     * Transport for the MCP connection.
     * - `http` / `sse`: user-registered servers (URL-based only — no process spawning).
     * - `stdio`: reserved for system-internal connectors registered at deploy time.
     */
    transport: z.enum(['stdio', 'http', 'sse']),
    command: z.string().optional(),
    args: z.array(z.string()).default([]),
    url: z.string().url().optional(),
    enabled: z.boolean().default(true),
    /** Internal system connectors (`true`) bypass user-facing transport restrictions. */
    system: z.boolean().default(false),
  })
  .merge(timestampsSchema);
export type McpServer = z.infer<typeof mcpServerSchema>;

/** User-facing MCP server registration — URL transports only, no process spawning. */
export const createMcpServerSchema = mcpServerSchema.pick({
  name: true,
  transport: true,
  url: true,
  enabled: true,
}).refine(
  (d) => d.transport !== 'stdio',
  { message: 'stdio transport is reserved for system connectors; use http or sse' },
);
export type CreateMcpServer = z.infer<typeof createMcpServerSchema>;
